import React from 'react';
import { CTASection } from '../../components/CTASection';
import { CreditCard, CheckCircle, Sparkles, Award, Users, Zap, Tag } from 'lucide-react';
export function BusinessCards() {
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1178" />,
    title: 'Custom Designs',
    description: 'From sleek and modern to fun and whimsical—our in-house designers bring your ideas to life'
  }, {
    icon: <Award className="w-6 h-6" data-id="element-1179" />,
    title: 'Quality Materials',
    description: 'High-quality printing on materials that look great and stand the test of time'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-1180" />,
    title: 'Quick Turnaround',
    description: 'Everything done in-house means no more waiting weeks—we deliver fast'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-1181" />,
    title: 'Excellent Service',
    description: 'From concept to delivery, we provide exceptional customer support every step of the way'
  }];
  const cardOptions = [{
    icon: '💼',
    title: 'Standard Business Cards',
    description: 'Classic, professional cards perfect for networking and first impressions'
  }, {
    icon: '✨',
    title: 'Premium Finishes',
    description: 'Matte, glossy, or textured finishes that add extra flair to your brand'
  }, {
    icon: '🎨',
    title: 'Custom Shapes',
    description: 'Stand out with unique die-cut shapes that reflect your brand personality'
  }, {
    icon: '🌟',
    title: 'Specialty Options',
    description: 'Foil stamping, embossing, and other premium touches available'
  }];
  const labelOptions = [{
    icon: '🏷️',
    title: 'Product Labels',
    description: 'Professional labels for packaging, bottles, jars, and retail products'
  }, {
    icon: '📦',
    title: 'Shipping Labels',
    description: 'Durable labels for packages and shipping needs'
  }, {
    icon: '🎁',
    title: 'Custom Stickers',
    description: 'Branded stickers for marketing, giveaways, or product sealing'
  }, {
    icon: '🔖',
    title: 'Specialty Labels',
    description: 'Waterproof, UV-resistant, or removable options for any application'
  }];
  const applications = ['Networking events', 'Trade shows', 'Client meetings', 'Product packaging', 'Retail displays', 'Marketing campaigns', 'Brand identity', 'Contact information'];
  return <div className="flex flex-col w-full" data-id="element-1182">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1183">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1184">
          <div className="flex justify-center mb-6" data-id="element-1185">
            <CreditCard className="w-16 h-16 text-white" data-id="element-1186" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1187">
            Business Cards & Product Labels
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1188">
            Your brand, perfectly packaged! Make your mark with eye-catching
            business cards and product labels that leave a lasting impression.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1189">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1190">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1191">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1192">
              First Impressions Matter
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1193">
              <p data-id="element-1194">
                In the world of business, first impressions matter. That's why
                we specialize in crafting eye-catching business cards and
                product labels that represent your brand in style! Whether
                you're a solopreneur or a growing company, we have everything
                you need to leave a lasting impression.
              </p>
              <p data-id="element-1195">
                With everything done in-house, we make it easy to create
                products that reflect your brand's unique personality. Our
                in-house graphic designers work with you to bring your ideas to
                life, and our quick turnarounds mean no more waiting weeks for
                your materials. From sleek and modern to fun and whimsical,
                we've got you covered!
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1196">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1197">
                <div className="flex items-start gap-4" data-id="element-1198">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1199">
                    {benefit.icon}
                  </div>
                  <div data-id="element-1200">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1201">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1202">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Business Cards Section */}
          <div className="mb-16" data-id="element-1203">
            <div className="flex items-center gap-3 mb-8" data-id="element-1204">
              <CreditCard className="w-8 h-8 text-wh-teal" data-id="element-1205" />
              <h2 className="text-3xl font-bold text-gray-900" data-id="element-1206">
                Business Card Options
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1207">
              {cardOptions.map((option, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors text-center" data-id="element-1208">
                  <div className="text-4xl mb-4" data-id="element-1209">{option.icon}</div>
                  <h3 className="text-lg font-bold text-wh-teal mb-2" data-id="element-1210">
                    {option.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1211">{option.description}</p>
                </div>)}
            </div>
          </div>

          {/* Product Labels Section */}
          <div className="mb-16" data-id="element-1212">
            <div className="flex items-center gap-3 mb-8" data-id="element-1213">
              <Tag className="w-8 h-8 text-wh-pink" data-id="element-1214" />
              <h2 className="text-3xl font-bold text-gray-900" data-id="element-1215">
                Product Label Options
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1216">
              {labelOptions.map((option, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-pink transition-colors text-center" data-id="element-1217">
                  <div className="text-4xl mb-4" data-id="element-1218">{option.icon}</div>
                  <h3 className="text-lg font-bold text-wh-pink mb-2" data-id="element-1219">
                    {option.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1220">{option.description}</p>
                </div>)}
            </div>
          </div>

          {/* Versatile Applications */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1221">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1222">
              Versatile Applications
            </h2>
            <p className="text-xl text-white/90 text-center mb-8 max-w-3xl mx-auto" data-id="element-1223">
              Perfect for networking, marketing your products, or simply sharing
              your contact information—our cards and labels are designed for any
              occasion.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto" data-id="element-1224">
              {applications.map((application, index) => <div key={index} className="flex items-center gap-2 bg-white/10 rounded-lg p-3" data-id="element-1225">
                  <CheckCircle className="w-5 h-5 text-wh-yellow flex-shrink-0" data-id="element-1226" />
                  <span className="text-sm font-medium" data-id="element-1227">{application}</span>
                </div>)}
            </div>
          </div>

          {/* Design Process */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1228">
            <div className="bg-white rounded-xl p-8 border-2 border-wh-pink text-center" data-id="element-1229">
              <div className="w-16 h-16 bg-wh-pink rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-1230">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1231">
                Share Your Vision
              </h3>
              <p className="text-gray-700" data-id="element-1232">
                Tell us about your brand, style preferences, and what you need
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-teal text-center" data-id="element-1233">
              <div className="w-16 h-16 bg-wh-teal rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-1234">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1235">
                We Design & Refine
              </h3>
              <p className="text-gray-700" data-id="element-1236">
                Our designers create custom options and work with you to perfect
                them
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-yellow text-center" data-id="element-1237">
              <div className="w-16 h-16 bg-wh-yellow rounded-full flex items-center justify-center text-gray-900 text-2xl font-bold mx-auto mb-4" data-id="element-1238">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1239">
                Print & Deliver
              </h3>
              <p className="text-gray-700" data-id="element-1240">
                High-quality printing with quick turnaround and fast delivery
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-1241">
            <Sparkles className="w-16 h-16 mx-auto mb-6" data-id="element-1242" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1243">
              Let's Get Started!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1244">
              Ready to elevate your branding game? Contact us today to design
              business cards and product labels that will leave a lasting
              impression!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1245">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1246">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1247">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1248" />
    </div>;
}